/**
 * LanguageContext — provides the active UI language and a `t()` translation
 * function to the entire KinShield app.
 *
 * Usage:
 *   import { useTranslation } from "../../i18n/LanguageContext";
 *   const { t, language, setLanguage } = useTranslation();
 *   <h1>{t("settings.title")}</h1>
 *
 * Behaviour:
 *  - Default language = value stored in localStorage("language") or "en".
 *  - Recognized language codes: "en", "en-gb", "es", "fr", "de" — anything
 *    starting with "es" maps to the Spanish dictionary; everything else
 *    falls back to English.
 *  - `setLanguage(code)` persists to localStorage and re-renders consumers
 *    immediately (no page reload required).
 *  - `t(key)` returns the dictionary value, or the key itself if unknown,
 *    so partial translation coverage is safe and visible. An optional second
 *    argument `t(key, params)` interpolates `{placeholder}` tokens in the
 *    resolved string with values from the `params` object.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import en from "./en";
import es from "./es";

const DICTIONARIES = { en, es };

/** Map any user-selected language code to one of the supported dictionaries. */
const resolveDictKey = (code) => {
  if (!code) return "en";
  const lower = String(code).toLowerCase();
  if (lower.startsWith("es")) return "es";
  // en, en-gb, fr, de, ... → fall back to English for now.
  return "en";
};

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      return localStorage.getItem("language") || "en";
    } catch {
      return "en";
    }
  });

  // Keep the document <html lang="..."> attribute in sync for accessibility.
  useEffect(() => {
    try {
      document.documentElement.lang = resolveDictKey(language);
    } catch {
      /* no-op (SSR / non-DOM environments) */
    }
  }, [language]);

  // React to language changes made elsewhere (e.g. UserSettingsAPI writing
  // to localStorage during settings load from the backend).
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "language" && event.newValue) {
        setLanguageState(event.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setLanguage = useCallback((code) => {
    const next = code || "en";
    try {
      localStorage.setItem("language", next);
    } catch {
      /* ignore storage errors */
    }
    setLanguageState(next);
  }, []);

  const dict = DICTIONARIES[resolveDictKey(language)] || en;

  const t = useCallback(
    (key, params) => {
      if (key == null) return "";
      const value = dict[key];
      let result = value != null ? value : key;
      if (params && typeof result === "string") {
        result = result.replace(/\{(\w+)\}/g, (match, name) =>
          Object.prototype.hasOwnProperty.call(params, name)
            ? String(params[name])
            : match,
        );
      }
      return result;
    },
    [dict],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}

export default LanguageContext;
